/**
 * Overview tab — property description + key facts. The mockup also includes
 * a full Neighborhood Analytics section (walk/transit/safety/schools); for
 * Phase 3 we use the existing NeighborhoodAnalyticsCard component when one
 * is available, falling back to a stub.
 */
import { Building2, Info } from "lucide-react";

import type { PropertySnapshot } from "../detail-helpers";

interface OverviewPanelProps {
  snapshot: PropertySnapshot;
  yearBuilt?: number | null;
}

export function OverviewPanel({ snapshot, yearBuilt }: OverviewPanelProps) {
  return (
    <div className="flex flex-col gap-4">
      {/* Description */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <Info className="h-4 w-4 text-primary" aria-hidden />
          Property Description
        </h3>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {snapshot.bd ?? "—"}-bedroom property in a desirable neighborhood.
          Offers {snapshot.sqft ?? "—"} sqft of living space with strong rental
          demand in the area. Run the deal calculators on the right to validate
          your investment strategy.
        </p>
      </div>

      {/* Key facts */}
      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <Building2 className="h-4 w-4 text-primary" aria-hidden />
          Property Facts
        </h3>
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <Fact label="Property Type" value="Single Family" />
          {yearBuilt && <Fact label="Year Built" value={yearBuilt.toString()} />}
          {snapshot.sqft != null && (
            <Fact label="Living Area" value={`${snapshot.sqft} sqft`} />
          )}
          {snapshot.bd != null && (
            <Fact label="Bedrooms" value={snapshot.bd.toString()} />
          )}
          {snapshot.ba != null && (
            <Fact label="Bathrooms" value={snapshot.ba.toString()} />
          )}
          {snapshot.days && (
            <Fact label="Days on Market" value={snapshot.days} />
          )}
          <Fact label="Status" value="Active" tone="success" />
        </div>
      </div>
    </div>
  );
}

interface FactProps {
  label: string;
  value: string;
  tone?: "default" | "success";
}

function Fact({ label, value, tone }: FactProps) {
  return (
    <div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div
        className={
          tone === "success" ? "font-semibold text-success" : "font-semibold"
        }
      >
        {value}
      </div>
    </div>
  );
}
